var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { combineReducers, configureStore, createAction, createListenerMiddleware, isAnyOf, } from '@reduxjs/toolkit';
import { createReduxEnhancer } from '@sentry/react';
import { enhancer as withReduxEnhancer } from 'addon-redux';
import { createReduxHistoryContext } from 'redux-first-history';
import { createBrowserHistory } from 'history';
import { sendAnalyticsData } from 'hx-analytics';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER, } from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
var _a = createReduxHistoryContext({
    history: createBrowserHistory(),
}), createReduxHistory = _a.createReduxHistory, routerMiddleware = _a.routerMiddleware, routerReducer = _a.routerReducer;
var listenerMiddleware = createListenerMiddleware();
listenerMiddleware.startListening({
    matcher: isAnyOf(createAction('@@router/LOCATION_CHANGE'), createAction('widget/setHostDetails')),
    effect: function (action, _a) {
        var getState = _a.getState;
        return __awaiter(void 0, void 0, void 0, function () {
            var state, data;
            var _b, _c;
            return __generator(this, function (_d) {
                state = getState();
                data = {
                    sessionId: state.widget.sessionId,
                    source: {
                        hostname: ((_b = state.widget.hostDetails) === null || _b === void 0 ? void 0 : _b.source.hostname) || 'unknown',
                        origin: ((_c = state.widget.hostDetails) === null || _c === void 0 ? void 0 : _c.source.origin) || 'unknown',
                    },
                    path: state.router.location.pathname,
                    type: 'page-view',
                    message: '',
                };
                sendAnalyticsData(data);
                return [2 /*return*/];
            });
        });
    },
});
export var configureStoreWithRouter = function (options, persistConfig) {
    if (persistConfig === void 0) { persistConfig = {
        blacklist: {
            widget: ['sessionId'],
            dinTest: ['status', 'error', 'steps', 'initialized'],
            wizard: ['status'],
        },
    }; }
    var reducers = options.reducers, middleware = options.middleware, persist = options.persist;
    var persistedReducers = persist === false
        ? reducers
        : Object.entries(reducers).reduce(function (acc, _a) {
            var key = _a[0], value = _a[1];
            acc[key] = persistReducer({
                key: key,
                storage: storageSession,
                blacklist: persistConfig.blacklist[key],
            }, value);
            return acc;
        }, {});
    var rootReducer = combineReducers(__assign({ router: routerReducer }, (options.persist !== false ? persistedReducers : reducers)));
    var store = configureStore({
        reducer: rootReducer,
        middleware: function (getDefaultMiddleware) {
            var _a;
            return (_a = getDefaultMiddleware({
                serializableCheck: {
                    ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
                },
            })
                .prepend(listenerMiddleware.middleware)
                .prepend(routerMiddleware))
                .prepend.apply(_a, middleware);
        },
        devTools: process.env['NODE_ENV'] !== 'production' || options.devTools,
        enhancers: [createReduxEnhancer(), withReduxEnhancer],
    });
    var history = createReduxHistory(store);
    var persistor = options.persist !== false ? persistStore(store) : null;
    return {
        store: store,
        history: history,
        persistor: persistor,
    };
};
